import { Input } from "../ui/input";
import { Button } from "../ui/button";
import { X, Plus } from "lucide-react";

// Structured resistances/weaknesses/immunities used by automated trap/hazard damage so it can
// self-adjust against a matched damage type (case-insensitive), instead of relying on the freeform
// notes textarea. Paper characters simply have empty lists here — the GM can always override HP by hand.
function AmountRow({ row, onChange, onRemove, testPrefix }) {
  return (
    <div className="flex items-center gap-1.5">
      <Input
        data-testid={`${testPrefix}-type-input`}
        value={row.type || ""}
        onChange={(e) => onChange({ ...row, type: e.target.value })}
        placeholder="fire, poison…"
        className="bg-black/30 border-white/10 h-7 text-xs flex-1"
      />
      <Input
        data-testid={`${testPrefix}-amount-input`}
        type="number"
        value={row.amount ?? 0}
        onChange={(e) => onChange({ ...row, amount: parseInt(e.target.value, 10) || 0 })}
        className="bg-black/30 border-white/10 h-7 w-16 text-xs"
      />
      <button data-testid={`${testPrefix}-remove-button`} onClick={onRemove} className="text-[#f1e0c5]/40 hover:text-[#D90429]">
        <X className="w-3 h-3" />
      </button>
    </div>
  );
}

export default function DamageMitigationEditor({ character, patchChar }) {
  const resistances = character.damage_resistances || [];
  const weaknesses = character.damage_weaknesses || [];
  const immunities = character.damage_immunities || [];

  const setList = (key, list) => patchChar({ [key]: list });

  return (
    <div className="space-y-2 border-t border-white/10 pt-3" data-testid="damage-mitigation-editor">
      <p className="text-[10px] uppercase text-[#f1e0c5]/40">Automated Resistances / Weaknesses (used by trap &amp; hazard damage)</p>
      {[
        { key: "damage_resistances", label: "Resistant to", list: resistances },
        { key: "damage_weaknesses", label: "Weak to", list: weaknesses },
      ].map(({ key, label, list }) => (
        <div key={key} className="space-y-1">
          <p className="text-[9px] uppercase text-[#f1e0c5]/30">{label}</p>
          {list.map((row, i) => (
            <AmountRow
              key={i}
              row={row}
              testPrefix={`${key}-${i}`}
              onChange={(updated) => setList(key, list.map((r, idx) => (idx === i ? updated : r)))}
              onRemove={() => setList(key, list.filter((_, idx) => idx !== i))}
            />
          ))}
          <Button
            data-testid={`${key}-add-button`}
            variant="ghost" size="sm"
            onClick={() => setList(key, [...list, { type: "", amount: 5 }])}
            className="h-6 text-[10px] gap-1 text-[#D4AF37]/70 hover:text-[#D4AF37]"
          >
            <Plus className="w-3 h-3" /> Add
          </Button>
        </div>
      ))}
      <div className="space-y-1">
        <p className="text-[9px] uppercase text-[#f1e0c5]/30">Immune to</p>
        <div className="flex flex-wrap gap-1.5">
          {immunities.map((type, i) => (
            <span key={i} className="flex items-center gap-1 bg-black/30 border border-white/10 rounded-full px-2 py-0.5 text-[10px]" data-testid={`damage-immunities-${i}`}>
              {type}
              <button onClick={() => setList("damage_immunities", immunities.filter((_, idx) => idx !== i))} className="text-[#f1e0c5]/40 hover:text-[#D90429]">
                <X className="w-2.5 h-2.5" />
              </button>
            </span>
          ))}
        </div>
        <Input
          data-testid="damage-immunities-add-input"
          placeholder="Type a damage type and press Enter (e.g. poison)"
          className="bg-black/30 border-white/10 h-7 text-xs"
          onKeyDown={(e) => {
            if (e.key === "Enter" && e.currentTarget.value.trim()) {
              setList("damage_immunities", [...immunities, e.currentTarget.value.trim()]);
              e.currentTarget.value = "";
            }
          }}
        />
      </div>
    </div>
  );
}
